import type { DocNode } from '@atlaskit/adf-schema';
import type { Fragment } from '@atlaskit/editor-prosemirror/model';
import type { RateLimiter } from '@atlassian/editor-ai-common/api/rate-limiter';

import type { ParagraphChunk } from '../../../types';
import type { TriggerContext } from '../commands';

import { requestGenerator } from './request-generator';

/**
 * Transformation actions for the proactive suggestions
 */
export type TransformAction =
	| 'CONVERT_TO_TABLE'
	| 'CONVERT_TO_NOTE_PANEL'
	| 'CONVERT_TO_INFO_PANEL'
	| 'CONVERT_TO_WARN_PANEL'
	| 'CONVERT_TO_ERROR_PANEL'
	| 'CONVERT_TO_SUCCESS_PANEL'
	| 'SPLIT_PARAGRAPH'
	| 'FORMAT_PARAGRAPH';

export type RecommendationType =
	| 'CONVERT_TO_TABLE'
	| 'CONVERT_TO_PANEL'
	| 'FORMAT_PARAGRAPH'
	| 'SPLIT_PARAGRAPH';

/**
 * Transformation details for the proactive suggestions.
 */
export type RecommendationDetails = {
	[key: string]: string[] | string;
};

export interface RecommendationMetadata {
	details?: RecommendationDetails;
	// Ignored via go/ees007
	// eslint-disable-next-line @atlaskit/editor/enforce-todo-comment-format
	// TODO: Remove this once it is removed on the backend.
	transformAction?: TransformAction;
	type?: RecommendationType;
}

type TransformContent = {
	document: DocNode;
	getContent: () => Fragment;
};

/**
 * This is the block chunk id which contains this recommendation.
 */
export interface Recommendation extends RecommendationMetadata {
	/**
	 * This is the block chunk id which contains this recommendation.
	 */
	chunkId: string;
	filtered?: boolean;
	/**
	 * This is a unique id for the recommendation
	 */
	id: string;
	isViewed: boolean;
	/**
	 * This is the raw recommendation id supplied from the BE
	 */
	rid: string;
	/**
	 * A score in the range 0..100 inclusive representing the estimated quality of the recommendation.
	 */
	score: number;
	transformContent: TransformContent;
	transformType: 'REPLACE_PARAGRAPH';
}

export type ResponseState =
	| { recommendations: Recommendation[]; state: 'parsed' | 'cached' }
	| { duration: number; state: 'trackedDuration' }
	| { failedChunkIds: string[]; state: 'rate-limited' }
	| {
			errors: string[];
			failedChunkIds: string[];
			reason: 'network' | 'backend' | 'aborted' | 'parsing' | 'unhandled' | 'rate-limited';
			state: 'failed';
			statusCode: number;
	  }
	| {
			purgedChunkIds: string[];
			state: 'purged';
			totalParts: number;
			totalPurgedParts: number;
	  }
	| { state: 'done' };

export type RequestGenerator = AsyncGenerator<ResponseState>;

// NOTE: It's important that we maintain a consistent API regardless of which endpoint we hit.
// This is a generic interface we should be able to use for any version of the provider.
export type ProactiveAIProvider = (
	paragraphs: Array<ParagraphChunk>,
) => RequestGenerator | undefined;

/**
 * This will filter out chunks from being streamed to the service which are not valid.
 */
export const isChunkExcluded = (chunk: ParagraphChunk) =>
	chunk.from === chunk.to || !chunk.text || !chunk.text.trim().length;

/* istanbul ignore next */
// This V1 endpoint is the final endpoint we're targetting for hello release.
export const fetchAIParagraphs =
	(
		proactiveAIApiUrl: string,
		product: string,
		context: TriggerContext,
		rateLimiter: RateLimiter | undefined,
		// Ignored via go/ees005
		// eslint-disable-next-line @typescript-eslint/max-params
	): ProactiveAIProvider =>
	(paragraphs: Array<ParagraphChunk>) => {
		const included = paragraphs.filter((chunk) => !isChunkExcluded(chunk));
		if (!!included.length) {
			return requestGenerator({
				endpoint: proactiveAIApiUrl,
				paragraphs: included,
				product,
				context,
				rateLimiter,
			});
		}

		return;
	};
